import { useTranslation } from 'react-i18next';

interface DateNavigatorProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  viewerName?: string;
  viewerRole?: string;
}

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function isToday(dateStr: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  return dateStr === today;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export default function DateNavigator({
  selectedDate,
  onDateChange,
  viewerName,
  viewerRole,
}: DateNavigatorProps) {
  const { t } = useTranslation();
  const todayStr = new Date().toISOString().split('T')[0];
  const canGoForward = selectedDate < todayStr;
  const isOnToday = isToday(selectedDate);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
          <i className="ri-calendar-2-line text-secondary-700 text-base" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading font-700 text-foreground-900 text-base sm:text-lg leading-tight truncate">
              {isOnToday ? 'Today\'s Attendance' : 'Attendance Record'}
            </h2>
            {viewerName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-label font-medium rounded-full bg-accent-100 text-accent-800 border border-accent-200 whitespace-nowrap">
                <i className="ri-user-line text-xs" />
                {viewerName}
                {viewerRole && <span className="text-accent-500">· {viewerRole}</span>}
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-400 font-label mt-0.5 truncate">{formatDisplayDate(selectedDate)}</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        <button
          onClick={() => onDateChange(addDays(selectedDate, -1))}
          className="w-10 h-9 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap active:scale-95"
          title={t('attendance.previousDay')}
        >
          <i className="ri-arrow-left-s-line text-base" />
        </button>

        {!isOnToday && (
          <button
            onClick={() => onDateChange(todayStr)}
            className="px-2.5 py-1.5 text-xs font-label font-medium rounded-lg bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('common.today')}
          </button>
        )}

        <input
          type="date"
          value={selectedDate}
          max={todayStr}
          onChange={(e) => onDateChange(e.target.value)}
          className="flex-1 sm:flex-none px-3 py-1.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 cursor-pointer min-w-0"
        />

        <button
          onClick={() => onDateChange(addDays(selectedDate, 1))}
          disabled={!canGoForward}
          className="w-10 h-9 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
          title={t('attendance.nextDay')}
        >
          <i className="ri-arrow-right-s-line text-base" />
        </button>
      </div>
    </div>
  );
}